import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SERVICES } from "@/data/site";
import CountrySelect from "./CountrySelect";

/**
 * <QuoteDrawer /> — global slide-in-from-right drawer that hosts the quote form.
 * Any button on the site can trigger it via:
 *   window.dispatchEvent(new CustomEvent("open-quote-drawer", { detail: { service: "..." } }))
 * The user stays on the page they were reading and fills the form inline.
 */
const TIMELINES = [
  { v: "immediate", l: "Immediate" },
  { v: "1-month", l: "Within 1 month" },
  { v: "1-3-months", l: "1 – 3 months" },
  { v: "3-6-months", l: "3 – 6 months" },
  { v: "exploring", l: "Just exploring" },
];

export default function QuoteDrawer() {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", company: "",
    phoneDial: "+44", phoneLocal: "",
    country: "",
    service: SERVICES[0].name, timeline: "1-month", description: "",
  });

  useEffect(() => {
    const handler = (e) => {
      const svc = e?.detail?.service;
      if (svc) setForm((f) => ({ ...f, service: svc }));
      setSubmitted(false);
      setOpen(true);
    };
    window.addEventListener("open-quote-drawer", handler);
    return () => window.removeEventListener("open-quote-drawer", handler);
  }, []);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        company: form.company,
        phone: `${form.phoneDial} ${form.phoneLocal}`.trim(),
        country: form.country,
        service: form.service,
        timeline: form.timeline,
        description: form.description,
      };
      await api.post("/quotations", payload);
      setSubmitted(true);
      toast.success("Quote request submitted. Check your inbox.");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => setOpen(false);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[95]" data-testid="quote-drawer">
          {/* backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={close}
            className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
          />

          {/* drawer */}
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 260 }}
            className="absolute right-0 top-0 bottom-0 w-full sm:max-w-xl lg:max-w-2xl bg-background border-l border-border shadow-[0_25px_80px_-20px_rgba(0,0,0,0.4)] overflow-y-auto"
            role="dialog"
            aria-label="Get a quote"
          >
            {/* Header */}
            <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-6 md:px-10 py-5 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-primary">
                  <Sparkles className="w-3 h-3" /> Get a quote
                </div>
                <h2 className="mt-1 font-heading font-black text-2xl md:text-3xl tracking-tight leading-tight">
                  Tell us about your project.
                </h2>
              </div>
              <button
                onClick={close}
                data-testid="quote-drawer-close"
                className="shrink-0 w-10 h-10 rounded-full border border-border hover:bg-foreground/10 inline-flex items-center justify-center transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {submitted ? (
              <div className="px-6 md:px-10 py-16 text-center" data-testid="quote-drawer-submitted">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                </div>
                <div className="mt-6 font-heading font-black text-3xl tracking-tight">You&apos;re in.</div>
                <p className="mt-3 text-muted-foreground max-w-md mx-auto leading-relaxed">
                  A senior member of our team is reviewing your project. Expect a tailored proposal within 48 hours to <strong>{form.email}</strong>.
                </p>
                <button
                  onClick={close}
                  data-testid="quote-drawer-done"
                  className="mt-8 rounded-full px-6 py-3 border border-border text-sm hover:bg-foreground/5"
                >
                  Back to the site
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="px-6 md:px-10 py-8 space-y-5" data-testid="quote-drawer-form">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Fill this in once. Within 48 hours you&apos;ll have a tailored proposal — architecture, timeline, price.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Full name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="drawer-name" />
                  <Field label="Email" required type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="drawer-email" />
                  <Field label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="drawer-company" />
                  {/* Phone with dial-code picker */}
                  <div>
                    <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Phone</label>
                    <div className="mt-2 flex gap-2">
                      <div className="shrink-0 w-[120px]">
                        <CountrySelect
                          mode="dial"
                          value={form.phoneDial}
                          onChange={(dial) => setForm({ ...form, phoneDial: dial })}
                          testid="drawer-phone-dial"
                          placeholder="+"
                        />
                      </div>
                      <input
                        type="tel"
                        value={form.phoneLocal}
                        onChange={(e) => setForm({ ...form, phoneLocal: e.target.value })}
                        placeholder="7380 885 506"
                        className="flex-1 bg-background border border-border rounded-xl px-4 py-2.5 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                        data-testid="drawer-phone-input"
                      />
                    </div>
                  </div>
                  {/* Country — searchable */}
                  <div>
                    <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Country</label>
                    <div className="mt-2">
                      <CountrySelect
                        mode="country"
                        value={form.country}
                        onChange={(name) => setForm({ ...form, country: name })}
                        testid="drawer-country"
                        placeholder="Select your country"
                      />
                    </div>
                  </div>
                  <SelectField
                    label="Service needed" value={form.service} onChange={(v) => setForm({ ...form, service: v })}
                    options={SERVICES.map((s) => ({ v: s.name, l: s.name }))}
                    testid="drawer-service"
                  />
                  <SelectField label="Timeline" value={form.timeline} onChange={(v) => setForm({ ...form, timeline: v })} options={TIMELINES} testid="drawer-timeline" />
                </div>
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Project description</label>
                  <textarea
                    required rows={5}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="What are you trying to build? What outcomes matter?"
                    className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 resize-none"
                    data-testid="drawer-description"
                  />
                </div>
                <div className="sticky bottom-0 bg-background/95 backdrop-blur-sm -mx-6 md:-mx-10 px-6 md:px-10 py-4 border-t border-border">
                  <button
                    disabled={loading} type="submit"
                    className="w-full rounded-full px-6 py-4 bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 shadow-[0_15px_40px_-15px_hsl(var(--primary)/0.5)]"
                    data-testid="drawer-submit"
                  >
                    {loading ? "Submitting…" : "Submit request"} <ArrowRight className="w-4 h-4" />
                  </button>
                  <div className="mt-2 text-center text-[11px] text-muted-foreground">
                    Typical proposal turnaround · 48 hours
                  </div>
                </div>
              </form>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, value, onChange, required, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{label}{required && " *"}</label>
      <input
        required={required} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-2.5 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
        data-testid={`${testid}-input`}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{label}</label>
      <select
        value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-2.5 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
        data-testid={`${testid}-select`}
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}
